import {
  type AllianceEvent,
  AllianceEventSchema,
  AllianceInvitedSchema,
  type AllianceRejected,
  AllianceRejectedSchema,
  type AllianceState,
  AllianceStateSchema,
} from '@shefira/shared';

import type { ChatPanel } from './chat.js';
import { InvitePrompt } from './invite-prompt.js';
import { uiFormat } from './text.js';

/** The little of a Colyseus room this needs. */
interface AllianceRoom {
  send: (type: string, payload?: unknown) => void;
  onMessage: (type: string, callback: (payload: unknown) => void) => unknown;
}

/** What the chat says when something happens to an alliance. */
export function allianceEventText(event: AllianceEvent): string {
  switch (event.kind) {
    case 'invited':
      return uiFormat('Proposition d’alliance envoyée à {name}.', { name: event.name });
    case 'formed':
      return uiFormat('Alliance conclue avec le groupe de {name}.', { name: event.name });
    case 'declined':
      return uiFormat('{name} décline l’alliance.', { name: event.name });
    case 'expired':
      return uiFormat('{name} n’a pas répondu à la proposition d’alliance.', { name: event.name });
    case 'dissolved':
      return uiFormat('L’alliance avec le groupe de {name} est rompue.', { name: event.name });
  }
}

export const ALLIANCE_REFUSAL_TEXT: Record<AllianceRejected['reason'], string> = {
  invalid: 'Demande refusée.',
  'not-leader': 'Seul le chef du groupe peut faire ça.',
  'not-found': "Personne de ce nom n'est en jeu.",
  self: 'Tu ne peux pas faire ça avec toi-même.',
  'target-not-leader': 'Ce joueur ne mène pas de groupe.',
  'already-allied': 'Un des deux groupes a déjà une alliance.',
  'too-many': 'Deux groupes au plus, dix joueurs au plus.',
  'no-invite': "Cette proposition n'est plus valable.",
  'not-allied': "Ton groupe n'a pas d'alliance.",
  pending: 'Une proposition attend déjà sa réponse.',
};

/**
 * The other party of an alliance, as the party's frames show it: its
 * leader and its size. Undefined without an alliance.
 */
export function alliedParty(
  state: AllianceState,
  /** The leader of the player's own party. */
  leaderId: string | null,
): { leader: string; count: number } | undefined {
  if (state.parties.length < 2 || leaderId === null) return undefined;
  const other = state.parties.find((party) => party.leaderId !== leaderId);
  return other === undefined ? undefined : { leader: other.leader, count: other.members.length };
}

/**
 * Alliances (ADR 0068 §5, ADR 0071): two parties joined by their leaders
 * for Shefira's raid. A leader proposes to another party's leader from the
 * player menu; the other answers in a prompt; either leader ends it. The
 * server keeps it and says so; this shows its answers.
 */
export class AllianceClient {
  private state: AllianceState = { parties: [] };
  private readonly prompt: InvitePrompt;

  constructor(
    private readonly room: AllianceRoom,
    chat: ChatPanel,
    /** The alliance changed: the party's frames show the other party. */
    private readonly onChange: (state: AllianceState) => void,
  ) {
    this.prompt = new InvitePrompt((from, accept) => {
      room.send('alliance_answer', { from, accept });
    });
    room.onMessage('alliance', (payload) => {
      const parsed = AllianceStateSchema.safeParse(payload);
      if (!parsed.success) return;
      this.state = parsed.data;
      this.onChange(parsed.data);
    });
    room.onMessage('alliance_invited', (payload) => {
      const parsed = AllianceInvitedSchema.safeParse(payload);
      if (!parsed.success) return;
      this.prompt.show({
        from: parsed.data.from,
        text: uiFormat('{name} propose une alliance à ton groupe pour le raid de Shefira.', {
          name: parsed.data.name,
        }),
        expiresInMs: parsed.data.expiresInMs,
      });
    });
    room.onMessage('alliance_event', (payload) => {
      const parsed = AllianceEventSchema.safeParse(payload);
      if (parsed.success) chat.notice(allianceEventText(parsed.data));
    });
    room.onMessage('alliance_rejected', (payload) => {
      const parsed = AllianceRejectedSchema.safeParse(payload);
      if (parsed.success) chat.notice(ALLIANCE_REFUSAL_TEXT[parsed.data.reason]);
    });
  }

  get current(): AllianceState {
    return this.state;
  }

  invite(name: string): void {
    this.room.send('alliance_invite', { name });
  }

  leave(): void {
    this.room.send('alliance_leave', {});
  }

  destroy(): void {
    this.prompt.close();
  }
}
